import React from 'react';
import { CheckCircle, RotateCcw, Upload, RefreshCw } from 'lucide-react';
import { usePhotoMetadataContext } from '../../contexts/PhotoMetadataContext';

const ToggleButton = ({ isExpanded, label, onClick }) => (
  <button
    onClick={onClick}
    className="text-sm text-blue-600 hover:text-blue-800 flex items-center font-medium"
  >
    {isExpanded ? 'Less' : label}
    <svg
      className={`ml-1 w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </button>
);

const PhotoStatusFooter = ({
  result,
  isApproved,
  isExpanded,
  onToggleExpand,
  onApprove,
  onReject
}) => {
  const { applyingState, applyPhotoMetadata, undoApply } = usePhotoMetadataContext();
  const isApplying = applyingState[result.photoId];

  let statusContent = <span className="text-gray-500">Needs review</span>;

  if (result.undoneAt) {
    statusContent = (
      <span className="text-orange-600 font-medium flex items-center">
        <RotateCcw className="mr-1 h-4 w-4" />
        Restored original
      </span>
    );
  } else if (result.applied) {
    statusContent = (
      <div className="flex items-center space-x-2">
        <span className="text-green-600 font-medium flex items-center">
          <CheckCircle className="mr-1 h-4 w-4" />
          Applied to Photo
        </span>
        {(result.originalTitle || result.originalDescription) && (
          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
            Backup saved
          </span>
        )}
      </div>
    );
  } else if (isApproved) {
    statusContent = <span className="text-green-600 font-medium">Ready to send</span>;
  }

  const renderActions = () => {
    if (result.applied) {
      return (
        <>
          {(result.originalTitle || result.originalDescription) && (
            <button
              onClick={() => undoApply && undoApply(result.photoId)}
              disabled={isApplying}
              className="inline-flex items-center px-3 py-1 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {isApplying ? (
                <>
                  <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                  Undoing...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Undo
                </>
              )}
            </button>
          )}
          <span className="text-gray-300">|</span>
          <ToggleButton
            isExpanded={isExpanded}
            label="View"
            onClick={() => onToggleExpand(result.photoId)}
          />
        </>
      );
    }

    if (result.undoneAt) {
      return (
        <>
          <button
            onClick={() => applyPhotoMetadata && applyPhotoMetadata(result.photoId)}
            disabled={isApplying}
            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isApplying ? (
              <>
                <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Upload className="mr-1 h-4 w-4" />
                Re-apply
              </>
            )}
          </button>
          <span className="text-gray-300">|</span>
          <ToggleButton
            isExpanded={isExpanded}
            label="Edit"
            onClick={() => onToggleExpand(result.photoId)}
          />
        </>
      );
    }

    return (
      <>
        {!isApproved ? (
          <>
            <button
              onClick={() => onApprove(result.photoId)}
              className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="mr-1 h-4 w-4" />
              Approve
            </button>
            <button
              onClick={() => onReject(result.photoId)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Skip
            </button>
          </>
        ) : (
          <button
            onClick={() => onReject(result.photoId)}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Undo Approval
          </button>
        )}
        <span className="text-gray-300">|</span>
        <ToggleButton
          isExpanded={isExpanded}
          label="Edit"
          onClick={() => onToggleExpand(result.photoId)}
        />
      </>
    );
  };

  return (
    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
      <div className="flex items-center text-sm">{statusContent}</div>
      <div className="flex items-center space-x-3">{renderActions()}</div>
    </div>
  );
};

export default PhotoStatusFooter;
